/**
 * Houseplant v2 — Instafeed, reformatted as a marquee row
 *
 * The CSS (component-hp-instafeed.css) owns the layout; this owns the two things
 * CSS cannot do, and it does them in TWO SEPARATE PHASES on purpose:
 *
 *   1. TAG  — add `.hp-instafeed` to the app block as soon as its tiles exist,
 *             which is what scopes every CSS rule to this block and nothing
 *             else. Runs immediately, so the row is a single row with the right
 *             radius from the first paint.
 *   2. CLONE — append one copy of the tiles, so translating the track -50% lands
 *             on an identical frame and the marquee loops seamlessly. This has
 *             to WAIT for the real images.
 *
 * ── Why the phases are split ───────────────────────────────────────────────
 * Instafeed lazy-loads its tiles: until one scrolls into view it holds a 1×1
 * placeholder, and `img.complete` is TRUE for that placeholder — which is how
 * the first version cloned a set of blank tiles and left two empty, hoverable
 * rows under the real ones. The test is `naturalWidth > 1`.
 *
 * Doing both phases behind that test would have been worse: on a homepage this
 * long the feed is far below the fold, so nothing would be styled at all until
 * someone scrolled to it. Tagging early and cloning late keeps the row correct
 * throughout.
 *
 * ── It restyles someone else's markup ──────────────────────────────────────
 * See the risk note at the top of the stylesheet. Written to fail SAFE: no
 * tiles found means nothing is tagged and the app's own grid renders untouched.
 */
(function () {
  var BLOCK = '.shopify-app-block';
  var WRAPPER = '.instafeed-new-layout-wrapper';
  var ITEM = '.instafeed-new-layout-item';
  var CLONE = 'data-hp-instafeed-clone';
  var PIXELS_PER_SECOND = 40;

  function realImages(wrapper) {
    return Array.prototype.filter.call(
      wrapper.querySelectorAll('img:not([' + CLONE + '] img)'),
      function (img) { return img.complete && img.naturalWidth > 1; }
    );
  }

  /** Phase 2. Safe to call repeatedly; it no-ops once the clones are in. */
  function addClones(block) {
    var wrapper = block.querySelector(WRAPPER);
    if (!wrapper || wrapper.querySelector('[' + CLONE + ']')) return;

    var items = Array.prototype.filter.call(
      wrapper.querySelectorAll(ITEM),
      function (item) { return !item.hasAttribute(CLONE); }
    );
    if (!items.length) return;

    /* Every original must have resolved, not just one — cloning at the first
       load copies whichever tiles are still placeholders. */
    if (realImages(wrapper).length < items.length) return;

    var fragment = document.createDocumentFragment();
    items.forEach(function (item) {
      var copy = item.cloneNode(true);
      copy.setAttribute(CLONE, '');
      /* The same posts again: announced once, tabbed through once. */
      copy.setAttribute('aria-hidden', 'true');
      Array.prototype.forEach.call(copy.querySelectorAll('a'), function (a) {
        a.setAttribute('tabindex', '-1');
      });

      /* Hand the copy a resolved src. A node cloned in after the fact is not in
         Instafeed's lazy-load registry, so left alone it keeps the placeholder
         for good. */
      var sources = item.querySelectorAll('img');
      Array.prototype.forEach.call(copy.querySelectorAll('img'), function (img, i) {
        var from = sources[i];
        if (!from) return;
        var real = from.currentSrc || from.src;
        if (real) img.src = real;
        if (from.srcset) img.srcset = from.srcset;
        img.className = img.className.replace(/instafeed-lazy-image\S*/g, '').trim();
        img.removeAttribute('data-src');
        img.removeAttribute('loading');
      });

      fragment.appendChild(copy);
    });
    wrapper.appendChild(fragment);

    measure(block, wrapper, items);
  }

  /**
   * Publish the travel distance and the duration.
   *
   * The distance is ONE FULL SET of tiles in PIXELS, measured from the
   * originals — not `-50%` of the track. A percentage is only one set while
   * exactly one copy exists, and the app discards the clones whenever it
   * re-renders: measured with the clones gone, `-50%` was 1584 against a 3168
   * set, so the row snapped back halfway through every cycle. In pixels the
   * loop stays in step with the content no matter how many copies survive.
   *
   * A forced reflow, NOT requestAnimationFrame — rAF is throttled in a hidden
   * tab, where the measurement silently never ran and the duration stayed on
   * its fallback. Same trap as the carousel's FLIP.
   */
  function measure(block, wrapper, items) {
    void wrapper.offsetWidth;

    var gap = 0;
    if (items.length > 1) {
      gap = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().right;
    }
    var setWidth = items.reduce(function (total, item) {
      return total + item.getBoundingClientRect().width + gap;
    }, 0);
    if (setWidth <= 0) return;

    block.style.setProperty('--hp-instafeed-shift', Math.round(setWidth) + 'px');
    block.style.setProperty(
      '--hp-instafeed-duration',
      Math.round(setWidth / PIXELS_PER_SECOND) + 's'
    );
  }

  /** Phase 1. */
  function tag(block) {
    var wrapper = block.querySelector(WRAPPER);
    if (!wrapper || !wrapper.querySelector(ITEM)) return false;

    if (!block.classList.contains('hp-instafeed')) {
      block.classList.add('hp-instafeed');

      /* Re-clone whenever an image finishes loading. `capture: true` because
         `load` does not bubble, and this has to catch images that appear later
         as the visitor scrolls the feed into view. */
      block.addEventListener('load', function () { addClones(block); }, true);

      /* SELF-HEALING. The app rewrites the wrapper's children after first
         paint and the clones go with them — which is what left the loop
         travelling half a set. Watching the wrapper itself (not the whole
         document) re-runs the clone step the moment that happens, and it is a
         far cheaper observer than a document-wide one. */
      new MutationObserver(function () { addClones(block); })
        .observe(wrapper, { childList: true });

      /* Pause off-screen, exactly as the native marquee does. A permanently
         animating 3000px layer several screens below the fold is main-thread
         work for nobody's benefit. */
      if ('IntersectionObserver' in window) {
        block.classList.add('is-offscreen');
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            block.classList.toggle('is-offscreen', !entry.isIntersecting);
          });
        }, { rootMargin: '200px 0px' }).observe(block);
      }
    }
    /* MEASURED HERE, not only after cloning. The travel distance depends on the
       ORIGINAL tiles, which exist as soon as the block is tagged — and if it
       waited for the clones (which wait for images) a slow feed would animate
       on the `50%` fallback in the meantime, which is precisely the half-a-set
       snap this replaced. Re-measured after cloning too, in case layout moved. */
    var originals = Array.prototype.filter.call(
      wrapper.querySelectorAll(ITEM),
      function (item) { return !item.hasAttribute(CLONE); }
    );
    measure(block, wrapper, originals);

    addClones(block);
    return true;
  }

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll(BLOCK), function (block) {
      if (block.querySelector('#insta-feed') || block.id.indexOf('instafeed') > -1) {
        tag(block);
      }
    });
  }

  function start() {
    scan();
    /* Watches only for the app block first APPEARING. Once tagged, the
       per-wrapper observer above handles re-renders, so this one stays cheap —
       measured at 0 mutation records over 10s on a settled page. */
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
